import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

/**
 * Length bounds enforced by the API. Kept as named constants so the forms and
 * the error messages cannot drift apart from each other.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/**
 * Client-side mirror of the API's password policy.
 *
 * The authoritative rules live in the backend's `PasswordPolicy` class
 * (`HealthcareStaffing.Application/Security/PasswordPolicy.cs`) and are enforced
 * on every registration and password-reset request. These validators exist only
 * so the user is told about a broken rule while typing, instead of by a 400 at
 * the end of a multi-step flow.
 *
 * Because of that, **a rule that is looser here than on the server is a bug**:
 * the form accepts the password, the request fails, and the message comes back
 * attached to a step the user cannot edit. Any change to the server policy
 * belongs here too. Being *stricter* than the server is merely annoying, so
 * where the two cannot be matched exactly this errs on the strict side.
 *
 * One server rule is deliberately not mirrored: the common-password denylist.
 * That is data rather than logic — the server's own comment says it should move
 * to storage so it can change without a deploy — and a copy here would be the
 * very thing that drifts out of date. Nothing on that list can reach the API
 * today, because every entry long enough to pass the length rule has no special
 * character and fails that rule first. If one ever does get through, the server
 * still rejects it, and the reason now reaches the user on the password field
 * rather than as a generic failure.
 */
export class PasswordValidators {
  /**
   * Validator that checks if the control's value contains at least one uppercase letter (A-Z).
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasUppercase);
   * ```
   *
   * @returns A validation error `{ missingUppercase: true }` if invalid, otherwise `null`.
   */
  static hasUppercase(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    return /[A-Z]/.test(value) ? null : { missingUppercase: true };
  }

  /**
   * Validator that checks if the control's value contains at least one lowercase letter (a-z).
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasLowercase);
   * ```
   *
   * @returns A validation error `{ missingLowercase: true }` if invalid, otherwise `null`.
   */
  static hasLowercase(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    return /[a-z]/.test(value) ? null : { missingLowercase: true };
  }

  /**
   * Validator that checks if the control's value contains at least one number (0-9).
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasDigit);
   * ```
   *
   * @returns A validation error `{ missingDigit: true }` if invalid, otherwise `null`.
   */
  static hasDigit(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    return /[0-9]/.test(value) ? null : { missingDigit: true };
  }

  /**
   * Validator that checks if the control's value contains at least one special
   * character — any printable ASCII character that is not a letter or a digit,
   * space included.
   *
   * The server rule is "not every character is a letter or digit", which is
   * Unicode-aware. Matching it exactly would need `\p{...}` escapes, which are
   * ES2018 syntax and unavailable at this project's compile target, so this is
   * limited to ASCII. That leaves it stricter than the server and never looser:
   * a non-ASCII symbol such as `€` is refused here although the API would take
   * it, but nothing accepted here is rejected there. Widening it instead to
   * "any non-alphanumeric" would break that guarantee, since the server counts
   * a letter like `ñ` as a letter and would reject a password relying on it.
   *
   * The previous fixed list omitted `~`, `'`, `"`, `/`, `\`, backtick and space,
   * all of which the API has always accepted.
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasSpecialChar);
   * ```
   *
   * @returns A validation error `{ missingSpecialChar: true }` if invalid, otherwise `null`.
   */
  static hasSpecialChar(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    // The ASCII ranges that sit between the alphanumerics: space-/ : -@ [-` {-~
    return /[ -/:-@[-`{-~]/.test(value) ? null : { missingSpecialChar: true };
  }

  /**
   * Validator that checks if the control's value contains three characters that
   * run in sequence, in **either** direction — "abc" and "cba", "123" and "321".
   *
   * Comparison is case-insensitive and made on character codes, so the run is
   * detected wherever it appears rather than only within the alphabet or the
   * digits. This matches the server rule exactly, including its side effect that
   * a run of adjacent punctuation such as "()*" also counts.
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(noSequentialChars);
   * ```
   *
   * @returns A validation error `{ sequentialChars: true }` if a run is found, otherwise `null`.
   */
  static noSequentialChars(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';
    if (value.length < 3) return null;

    const lowered = value.toLowerCase();

    for (let i = 0; i < lowered.length - 2; i++) {
      const first = lowered.charCodeAt(i);
      const second = lowered.charCodeAt(i + 1);
      const third = lowered.charCodeAt(i + 2);

      // abc, 123
      if (second === first + 1 && third === second + 1) {
        return { sequentialChars: true };
      }
      // cba, 321
      if (second === first - 1 && third === second - 1) {
        return { sequentialChars: true };
      }
    }

    return null;
  }

  /**
   * Validator that checks if the control's value repeats the same character more
   * than twice in a row, such as "aaa". Comparison is case-insensitive, so "aAa"
   * counts as a repeat.
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(noRepeatingChars);
   * ```
   *
   * @returns A validation error `{ repeatingChars: true }` if a repeat is found, otherwise `null`.
   */
  static noRepeatingChars(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';
    if (value.length < 3) return null;

    const lowered = value.toLowerCase();

    for (let i = 0; i < lowered.length - 2; i++) {
      if (lowered[i] === lowered[i + 1] && lowered[i + 1] === lowered[i + 2]) {
        return { repeatingChars: true };
      }
    }

    return null;
  }
}

/**
 * The whole password policy, as one list to spread into a control.
 *
 * Every password field in every app uses this, so that the rules are written
 * once rather than copied into each form — the copies are what let the client
 * and the server disagree in the first place.
 *
 * `Validators.required` is deliberately **not** included: presence is a separate
 * concern, and reporting "too short" for a field the user has not filled in yet
 * is noise. Compose it at the call site:
 *
 * @example
 * ```typescript
 * password: new FormControl<string | null>(null, [
 *   Validators.required,
 *   ...PASSWORD_POLICY_VALIDATORS
 * ])
 * ```
 */
export const PASSWORD_POLICY_VALIDATORS: ValidatorFn[] = [
  Validators.minLength(PASSWORD_MIN_LENGTH),
  Validators.maxLength(PASSWORD_MAX_LENGTH),
  PasswordValidators.hasLowercase,
  PasswordValidators.hasUppercase,
  PasswordValidators.hasDigit,
  PasswordValidators.hasSpecialChar,
  PasswordValidators.noSequentialChars,
  PasswordValidators.noRepeatingChars
];
